import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { toast } from 'sonner';
import { submitLead, setSessionLead } from '../services/api';
import { countryCodes, workExperienceOptions, industryOptions } from '../data/formOptions';
import { countryList } from '../data/countryList';
import { ChevronDown, Check } from 'lucide-react';

const LeadFormModal = ({ isOpen, onClose, onSubmit, webinarTitle, webinarId }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+91',
    mobileNumber: '',
    country: '',
    workExperience: '',
    industry: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStartTime, setFormStartTime] = useState(null);
  const [prefilledName, setPrefilledName] = useState(null);
  const [prefilledEmail, setPrefilledEmail] = useState(null);
  const [isTestMode, setIsTestMode] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);

  // Validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Check for pre-filled name and email from URL parameters
  useEffect(() => {
    if (isOpen) {
      setFormStartTime(Date.now());
      
      // Check URL parameters for pre-filled values
      const urlParams = new URLSearchParams(window.location.search);
      
      // Handle name prefill
      const firstName = urlParams.get('first_name') || urlParams.get('firstName');
      const lastName = urlParams.get('last_name') || urlParams.get('lastName');
      const fullName = urlParams.get('name') || urlParams.get('fullName');
      
      let prefilledNameValue = fullName || (firstName && lastName ? `${firstName} ${lastName}`.trim() : firstName);
      
      if (prefilledNameValue) {
        setFormData(prev => ({ ...prev, name: prefilledNameValue }));
        setPrefilledName(prefilledNameValue);
      }
      
      // Handle email prefill
      const emailParam = urlParams.get('email');
      if (emailParam && isValidEmail(emailParam)) {
        setFormData(prev => ({ ...prev, email: emailParam }));
        setPrefilledEmail(emailParam);
      }
      
      // Check for test mode
      const testMode = window.location.hostname === 'localhost' || urlParams.get('test_mode') === 'true';
      setIsTestMode(testMode);
    }
  }, [isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.name || formData.name.trim().length < 2) {
      toast.error('Please enter your full name');
      return false;
    }
    
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    
    if (!formData.mobileNumber || formData.mobileNumber.trim().length < 7) {
      toast.error('Please enter a valid mobile number');
      return false;
    }

    if (!formData.country) {
      toast.error('Please select your country');
      return false;
    }
    
    if (!formData.workExperience) {
      toast.error('Please select your work experience');
      return false;
    }
    
    if (!formData.industry) {
      toast.error('Please select your industry');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Calculate form completion time
      const completionTime = formStartTime ? Math.floor((Date.now() - formStartTime) / 1000) : null;
      
      // Get UTM parameters from URL
      const urlParams = new URLSearchParams(window.location.search);
      const utmParams = {};
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(param => {
        if (urlParams.get(param)) {
          utmParams[param] = urlParams.get(param);
        }
      });
      
      // Submit to backend API
      const response = await submitLead({
        webinar_id: webinarId,
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        country_code: formData.countryCode,
        mobile_number: formData.mobileNumber.trim(),
        country: formData.country,
        work_experience: formData.workExperience,
        industry: formData.industry,
        source_url: window.location.href,
        utm_params: Object.keys(utmParams).length > 0 ? utmParams : null,
        prefilled_name: prefilledName,
        prefilled_email: prefilledEmail,
        is_test: window.location.hostname === 'localhost' || window.location.search.includes('test_mode=true'),
        form_completion_time: completionTime
      });

      if (response.success) {
        // Store lead data globally to unlock all webinars for this session
        setSessionLead({
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          submittedAt: new Date().toISOString(),
          firstWebinarId: webinarId,
        });

        if (response.is_duplicate) {
          toast.info('Welcome back! You already have access to this webinar.');
        } else {
          toast.success('Access granted! Enjoy the webinar.');
        }
        onSubmit(response);
        onClose();
      }
    } catch (error) {
      console.error('Lead submission error:', error);
      if (error.response?.status === 404) {
        toast.error('Webinar not found. Please try again.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || 'Invalid form data. Please check your inputs.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[540px] max-h-[90vh] overflow-y-auto" data-testid="lead-form-modal">
        <DialogHeader>
          <DialogTitle className="text-xl">Watch This Webinar</DialogTitle>
          <DialogDescription className="text-sm">
            Please provide your details to access <span className="font-medium text-gray-900">{webinarTitle}</span>
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="lead-form">
          {/* Test Mode Banner */}
          {isTestMode && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-2" data-testid="test-mode-banner">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-yellow-800">Test Mode Active</span>
              </div>
              <p className="text-xs text-yellow-700 mt-1">
                This submission will be marked as a test lead and won't affect your production data.
              </p>
            </div>
          )}
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              data-testid="lead-form-name"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
              className="h-11"
            />
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              data-testid="lead-form-email"
              type="email"
              placeholder="your.email@example.com"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
              className="h-11"
            />
          </div>

          {/* Mobile Number with Country Code */}
          <div className="space-y-2">
            <Label htmlFor="mobile">Mobile Number *</Label>
            <div className="flex gap-2">
              <Select value={formData.countryCode} onValueChange={(value) => handleChange('countryCode', value)}>
                <SelectTrigger className="w-[140px] h-11" data-testid="lead-form-country-code">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {countryCodes.map(({ code, country, flag }) => (
                    <SelectItem key={code} value={code}>
                      <span className="flex items-center gap-2">
                        <span>{flag}</span>
                        <span>{code}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                id="mobile"
                data-testid="lead-form-mobile"
                type="tel"
                placeholder="9876543210"
                value={formData.mobileNumber}
                onChange={(e) => handleChange('mobileNumber', e.target.value.replace(/\D/g, ''))}
                required
                className="flex-1 h-11"
                maxLength={15}
              />
            </div>
            <p className="text-xs text-gray-500">Enter mobile number without country code</p>
          </div>

          {/* Country */}
          <div className="space-y-2">
            <Label>Country *</Label>
            <Popover open={countryOpen} onOpenChange={setCountryOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={countryOpen}
                  className="w-full h-11 justify-between font-normal"
                  data-testid="lead-form-country"
                >
                  {formData.country || "Select Country"}
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search country..." />
                  <CommandList>
                    <CommandEmpty>No country found.</CommandEmpty>
                    <CommandGroup>
                      {countryList.map((c) => (
                        <CommandItem
                          key={c}
                          value={c}
                          onSelect={() => {
                            handleChange('country', c);
                            setCountryOpen(false);
                          }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${formData.country === c ? 'opacity-100' : 'opacity-0'}`} />
                          {c}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Work Experience */}
          <div className="space-y-2">
            <Label htmlFor="workExperience">Work Experience *</Label>
            <Select value={formData.workExperience} onValueChange={(value) => handleChange('workExperience', value)} required>
              <SelectTrigger className="h-11" data-testid="lead-form-experience">
                <SelectValue placeholder="Select your work experience" />
              </SelectTrigger>
              <SelectContent>
                {workExperienceOptions.map(option => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Industry */}
          <div className="space-y-2">
            <Label htmlFor="industry">Industry *</Label>
            <Select value={formData.industry} onValueChange={(value) => handleChange('industry', value)} required>
              <SelectTrigger className="h-11" data-testid="lead-form-industry">
                <SelectValue placeholder="Select your industry" />
              </SelectTrigger>
              <SelectContent>
                {industryOptions.map(option => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Privacy Note */}
          <p className="text-xs text-gray-500 pt-2">
            By submitting this form, you agree to receive communications from Great Learning. We respect your privacy and will never share your information.
          </p>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 h-11"
              disabled={isSubmitting}
              data-testid="lead-form-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 h-11 bg-blue-600 hover:bg-blue-700"
              disabled={isSubmitting}
              data-testid="lead-form-submit"
            >
              {isSubmitting ? 'Submitting...' : 'Watch Webinar'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LeadFormModal;
